import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  TabContent,
  TabPane,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import LeaderboardTable from "../components/LeaderboardTable";
import { getLeaderboardLeaders } from "@/services/program/getLeaderboardLeaders";

const getMetricMeta = (leaderboardTypeName = "") => {
  switch (leaderboardTypeName) {
    case "Goal Progress":
      return {
        label: "Progress",
        description: "Ranks participants by goal completion.",
      };
    case "Event Summary":
      return {
        label: "Award value",
        description: "Ranks participants by total award amount.",
      };
    case "Event Volume":
      return {
        label: "Award count",
        description: "Ranks participants by award volume.",
      };
    default:
      return {
        label: "Awards",
        description: "Ranks participants by award activity.",
      };
  }
};

const formatMetricValue = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return "0";
  }

  return Math.round(numericValue).toLocaleString();
};

const Leaderboards = ({ program, organization }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [leaderboardLeaders, setLeaderboardLeaders] = useState([]);

  useEffect(() => {
    if (!organization?.id || !program?.id) {
      return;
    }

    setLoading(true);
    getLeaderboardLeaders(organization.id, program.id)
      .then((items) => {
        setLeaderboardLeaders(items || []);
        setLoading(false);
      })
      .catch(() => {
        setLeaderboardLeaders([]);
        setLoading(false);
      });
  }, [organization?.id, program?.id]);

  useEffect(() => {
    if (activeTab > Math.max(leaderboardLeaders.length - 1, 0)) {
      setActiveTab(0);
    }
  }, [activeTab, leaderboardLeaders.length]);

  const activeLeaderboard = leaderboardLeaders[activeTab] || null;
  const activeLeaders = activeLeaderboard?.leaders || [];
  const topLeader = activeLeaders[0] || null;
  const topThreeLeaders = activeLeaders.slice(0, 3);
  const leaderboardCount = leaderboardLeaders.length;

  const metricMeta = useMemo(
    () => getMetricMeta(activeLeaderboard?.leaderboard_type?.name),
    [activeLeaderboard?.leaderboard_type?.name]
  );

  const totalMetric = useMemo(() => {
    return activeLeaders.reduce((sum, item) => sum + Number(item?.total || 0), 0);
  }, [activeLeaders]);

  const toggle = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const renderStateCard = (title, description) => (
    <Container className="manager-home__shell manager-home__content">
      <section className="manager-leaderboards manager-leaderboards--state">
        <div className="manager-section-label">
          <div className="manager-section-label__left">
            <span>{t("leaderboards")}</span>
          </div>
          <span>Program rankings</span>
        </div>
        <div className="manager-leaderboards__state-card">
          <div className="manager-leaderboards__state-content">
            <p className="manager-leaderboards__eyebrow">Leaderboard overview</p>
            <h1 className="manager-leaderboards__title">{title}</h1>
            <p className="manager-leaderboards__description">{description}</p>
          </div>
        </div>
      </section>
    </Container>
  );

  if (loading) {
    return renderStateCard(t("loading"), "Loading leaderboard performance data.");
  }

  if (leaderboardLeaders.length === 0) {
    return renderStateCard("No leaderboards yet", "Leaderboard data will appear here once participants begin ranking.");
  }

  return (
    <Container className="manager-home__shell manager-home__content">
      <section className="manager-leaderboards">
        <div className="manager-section-label">
          <div className="manager-section-label__left">
            <span>{t("leaderboards")}</span>
          </div>
          <span>{leaderboardCount} {leaderboardCount === 1 ? "board" : "boards"}</span>
        </div>

        <div className="manager-leaderboards__hero">
          <div className="manager-leaderboards__hero-copy">
            <h1 className="manager-leaderboards__title">
              {activeLeaderboard?.name || t("leaderboards")}
            </h1>
            <p className="manager-leaderboards__description">
              Switch between leaderboard views, track the current front-runners,
              and open any participant row to review award history.
            </p>
          </div>

          <div className="manager-leaderboards__stats">
            <div className="manager-leaderboards__stat">
              <span className="manager-leaderboards__stat-label">Board count</span>
              <strong className="manager-leaderboards__stat-value">{leaderboardCount}</strong>
            </div>
            <div className="manager-leaderboards__stat">
              <span className="manager-leaderboards__stat-label">Ranked participants</span>
              <strong className="manager-leaderboards__stat-value">{activeLeaders.length}</strong>
            </div>
            <div className="manager-leaderboards__stat">
              <span className="manager-leaderboards__stat-label">Top performer</span>
              <strong className="manager-leaderboards__stat-value manager-leaderboards__stat-value--name">
                {topLeader?.display_name || "No participants"}
              </strong>
            </div>
            <div className="manager-leaderboards__stat">
              <span className="manager-leaderboards__stat-label">{metricMeta.label}</span>
              <strong className="manager-leaderboards__stat-value">
                {formatMetricValue(topLeader?.total)}
              </strong>
            </div>
          </div>
        </div>

        <div className="manager-leaderboards__podium">
          {topThreeLeaders.map((leader, index) => (
            <div
              className={`manager-leaderboards__podium-card manager-leaderboards__podium-card--rank-${index + 1}`}
              key={leader.user_id || index}
            >
              <span className={`manager-leaderboards__podium-rank manager-leaderboards__podium-rank--rank-${index + 1}`}>
                #{index + 1}
              </span>
              <strong className="manager-leaderboards__podium-name">{leader.display_name}</strong>
              <span className="manager-leaderboards__podium-value">
                {formatMetricValue(leader.total)} {metricMeta.label}
              </span>
            </div>
          ))}
          {topThreeLeaders.length === 0 && (
            <div className="manager-leaderboards__podium-card manager-leaderboards__podium-card--empty">
              No ranked participants yet.
            </div>
          )}
        </div>

        <div className="leaderboards leaderboards--manager">
          <div className="leaderboards-panel leaderboards-panel--manager">
            {leaderboardLeaders.length > 1 && (
              <Nav tabs className="leaderboards-panel__tabs leaderboards-panel__tabs--manager">
                {leaderboardLeaders.map((item, index) => (
                  <NavItem key={item.id || index} className="leaderboards-panel__tab-item">
                    <NavLink
                      className={classnames("leaderboards-panel__tab leaderboards-panel__tab--manager", {
                        active: activeTab === index,
                      })}
                      onClick={() => {
                        toggle(index);
                      }}
                    >
                      {item.name}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
            )}

            <div className="manager-leaderboards__panel-head">
              <div className="manager-leaderboards__panel-copy">
                <p className="manager-leaderboards__panel-kicker">Active leaderboard</p>
                <h2 className="manager-leaderboards__panel-title">{activeLeaderboard?.name}</h2>
                <p className="manager-leaderboards__panel-description">{metricMeta.description}</p>
              </div>
              <div className="manager-leaderboards__panel-meta">
                <span className="manager-leaderboards__panel-meta-chip">
                  Total tracked: {formatMetricValue(totalMetric)}
                </span>
                <span className="manager-leaderboards__panel-hint">
                  Click a participant row to view award history.
                </span>
              </div>
            </div>

            <TabContent activeTab={activeTab} className="leaderboards-panel__content leaderboards-panel__content--manager">
              {leaderboardLeaders.map((item, index) => (
                <TabPane tabId={index} key={item.id || index}>
                  <div className="leaderboards-panel__table leaderboards-panel__table--manager">
                    <LeaderboardTable leaderboard={item} variant="manager" />
                  </div>
                </TabPane>
              ))}
            </TabContent>
          </div>
        </div>
      </section>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  program: state.program,
  organization: state.organization,
});

export default connect(mapStateToProps)(Leaderboards);
